/**
 * Tours Settings: UI strings for Tours > Settings pages.
 * Translations are applied conditionally in iframe mode only.
 */

export const toursSettingsEn = {
  toursSettings: {
    // Common
    common: {
      changesSaved: 'Changes Saved!',
      changesSavedMessage: 'Changes made to form has been successfully saved.',
      savingForm: 'Saving form',
      saving: 'Saving...',
      errorsBeforeSaving: 'You have some errors! Please fix before saving.',
      unsavedChanges: 'You have unsaved changes!',
      noPermission: 'You do not have permission to make changes',
      somethingWentWrong: 'Something went wrong',
      on: 'On',
      off: 'Off',
      enabled: 'Enabled',
      disabled: 'Disabled',
      active: 'Active',
      inactive: 'Inactive',
      select: 'Select',
      all: '[All]',
      notApplicable: 'N/A',
      properties: 'Properties',
      status: 'Status',
      remove: 'Remove',
      confirm: 'Confirm',
      example: 'Example',
      message: 'Message',
      gotIt: 'Got it',
      days: 'Days',
      custom: 'Custom',
      daysAvailable: 'Days Available',
      sundayInitial: 'S',
      mondayInitial: 'M',
      tuesdayInitial: 'T',
      wednesdayInitial: 'W',
      thursdayInitial: 'T',
      fridayInitial: 'F',
      saturdayInitial: 'S',
      sundayName: 'Sunday',
      mondayName: 'Monday',
      tuesdayName: 'Tuesday',
      wednesdayName: 'Wednesday',
      thursdayName: 'Thursday',
      fridayName: 'Friday',
      saturdayName: 'Saturday',
      to: 'To',
    },

    // Tour General Settings (tour-general/Index.vue)
    general: {
      brandSettings: 'Brand Settings',
      logo: 'Logo',
      logoTooltip: 'This logo is imported from your company profile. If you wish to make any changes please contact your Rentsync account manager.',
      backgroundColour: 'Background Colour',
      brandColourPreview: 'Brand Colour Preview',
      brandColourBanner: 'Please ensure that the text is clearly visible on your chosen brand colour in the test area below. The brand colour will be used throughout the Tours Booking Widget.',
      testTitle: 'Your test title here.',
      customMessageTitle: 'Custom message on Tours Widget',
      learnMoreTranslations: 'Learn More About Translations',
      learnMoreTranslationsTitle: 'Learn More - Translations',
      translationsInfo1: 'You have language options available. Please add translations to your custom message to display them on the tours booking widget used on your connected website(s).',
      translationsInfo2: 'Please note that if translations are not provided, the custom message on the booking widget will display the corresponding English values.',
      customMessageDescription: 'This message will appear at the top of each page on the Tours Booking Widget - Date, Time, and Form.',
      interpolationSupport: 'Tours widget custom message supports',
      interpolation: 'interpolation.',
      customMessageToggleDescription: 'If you would like to add a custom message on your Tours Booking Widget for prospects, please toggle this setting on and add your custom message below.',
      defaultCustomMessage: 'Book your tour today. We look forward to welcoming you.',
      setMessageBottom: 'Set message to bottom position in the Tour Booking Widget',
      setMessageBottomTooltip: 'By default, the custom message appears above the form. Enable this option to display the custom message below the form instead.',
      customMessageExampleTitle: 'Custom Message Tour Widget Example',
      tourGeneralTranslationsHeader: 'Tour General Settings Translations',
      // Color Picker Modal
      colorPickerTitle: 'Background Colour',
      colorPickerHex: 'Hex',
      colorPickerSave: 'Save',
      // Interpolation Modal
      interpolationTitle: 'Interpolation',
      interpolationDescription: 'This refers to adding dynamic data from your form into otherwise static text.',
      interpolationKeyInstruction: 'All interpolation is done by adding a "key" in double square brackets within your text.',
      interpolationExample: 'Example: "Hi, [[full_name]]"',
      interpolationFormNameInfo: 'The key can be the "Form Name" of any field in your form (located under "Show Advanced"). If your Form Name says "[auto]", you can add one yourself or save the form to generate one first.',
      interpolationPredefinedKeys: 'Interpolation keys can also be one of the following predefined keys, which refer to the details of a property or city if one is selected in some way.',
      interpolationTour: 'Tour:',
      interpolationProperty: 'Property:',
      interpolationCity: 'City:',
      interpolationProspect: 'Prospect:',
    },

    // Agent Settings List (agent-settings/Index.vue)
    agentList: {
      defaultAgentSettings: 'Default Agent Settings',
      removeFilters: 'Remove',
      filters: 'Filters',
      filter: 'Filter',
      tourAgentHeader: 'Tour Agent',
      emailAddressHeader: 'Email Address',
      agentStatusHeader: 'Agent Status',
      agentStatusHeaderTooltip: 'Agent Status denotes if a Tour Agent is accepting tour bookings at this time or not.',
      availabilityHeader: 'Availability',
      numberOfPropertiesHeader: '# Of Properties',
    },

    // Agent Settings Single (agent-settings/single/Index.vue)
    agentSingle: {
      tourAgentProfile: 'Tour Agent Profile',
      agentStatusBanner: 'If the agent status is set to "Inactive" and the property does not have any other Active tour agent assigned, new tours can not be requested for that property.',
      firstName: 'First Name',
      lastName: 'Last Name',
      email: 'Email',
      agentStatus: 'Agent Status',
      agentStatusTooltip: 'Status denotes if a Tour Agent is conducting tour booking at this time or not.',
      calendarSyncSettings: 'Calendar Sync Settings',
      removeCalendars: 'Remove Calendars',
      reSyncCalendar: 'Re-sync Calendar',
      reSyncCalendarTooltip: 'Click to view an updated list of connected calendars',
      calendarDisconnected: 'Your linked external calendar has been disconnected. To reconnect, click on the button below.',
      connectCalendars: 'Connect your existing calendars from Google, Apple, Microsoft or Exchange. You can choose to share your bookings with your day-to-day calendar.',
      syncMyCalendar: 'Sync My Calendar',
      emailedEventsWarning: 'Emailed Events are read-only calendar. Tours created on Rentsync calendar will not be added to the Email Events calendar.',
      bulkCalendarSettings: 'Bulk Calendar Settings',
      custom: 'Custom',
      customTooltip: 'Use the toggles on the table to select calendar sync settings individually.',
      disableAllCalendars: 'Disable All Calendars',
      calendarSyncing: 'Calendar is currently syncing',
      calendarPending: 'Calendar sync is pending or disconnected',
      lastSynced: 'last synced',
      calendarSyncStatus: 'Calendar Sync Status',
      agentSettingsInternal: 'Agent Settings (Internal Use Only)',
      timezoneInternalBanner: 'Setting the timezone below will allow you to view the agent\'s availability and time blocks in the selected timezone. The system will convert and save the changes made in the default {timezone} timezone.',
      timezoneInternal: 'Timezone (Internal Use Only)',
      availability: 'Availability',
      setPerProperty: 'Set Per Property',
      setPerPropertyTooltip: 'This option is only available for agents assigned to multiple properties.',
      allTimesShownIn: 'All times shown in {timezone} timezone',
      availabilityDescription: 'This setting will allow prospect to book tours within set time range. All integrated calendars will be impacted.',
      timezoneUpdateBanner: 'There has been an update on the company\'s timezone. Re-save your settings to sync your availabilities to {timezone} timezone.',
      propertiesWithoutAvailability: 'Warning: You have properties without availability set. Please ensure your availability is correctly allocated.',
      addNewInterval: 'Add New Interval',
      copyTime: 'Copy Time',
      copied: 'Copied',
      pasteTime: 'Paste Time',
      conflictingIntervals: 'Conflicting intervals detected for the same property. Adjust times or remove property assignments before saving.',
      calendars: 'Calendars',
      syncingOptions: 'Syncing Options',
      syncCalendars: 'Sync Calendar(s)',
      importEventTitles: 'Import Event Titles',
      importEventTitlesTooltip: 'Turn on to import event titles from your calendar or it will show as a time block.',
      importNonBusyEvents: 'Import Non-Busy Events',
      importNonBusyEventsTooltip: 'Turn off to stop importing events from your calendar that are not marked as \'busy\'.',
      deleteCalendarTitle: 'Delete Calendar',
      deleteCalendarMessage: 'Deleting calendar will un-sync all your calendars from Rentsync Tours. Are you sure you want to continue?',
      deleteCalendarNote: 'Please press "Save" on the page after exiting the modal to apply changes.',
      disableCalendarTitle: 'Disable calendar from Rentsync Tours',
      disableCalendarMessage: 'Disabling calendar will prevent future events on selected calendar(s) from syncing with Rentsync Tours Calendar. Are you sure you want to continue?',
      propertiesWithoutAvailabilityTitle: 'Properties Without Availability',
      propertiesWithoutAvailabilityMessage: 'You have properties without availability set. This means tours cannot be booked for these properties. Are you sure you want to continue saving?',
      agentSettingsSaved: 'Changes made to Agent Settings have been successfully saved.',
      conflictingAvailabilities: 'Conflicting Availabilities',
      resolveOverlapping: 'Resolve overlapping intervals for the same property before saving.',
      sameAvailabilityEveryday: 'Same availability everyday',
      differentAvailabilityEveryday: 'Different availability everyday',
    },

    // Tour Properties List (tour-properties/Index.vue)
    propertiesList: {
      search: 'Search',
      cities: 'Cities',
      tourAgents: 'Tour Agents',
      toursStatus: 'Tours Status',
      bulkEditProperties: 'Bulk Edit Properties',
      defaultPropertySettings: 'Default Property Settings',
      missingAgentAvailability: 'Missing agent availability for one or more agents',
      propertiesSaved: 'Changes made to properties have been successfully saved.',
      enableTourStatus: 'Enable Tour Status',
      disableTourStatus: 'Disable Tour Status',
      bulkActionEnable: 'Enable',
      bulkActionDisable: 'Disable',
      bulkActionSuffix: 'tour status for',
      selectedProperties: 'Selected:',
      propertiesSuffix: 'properties',
      addressHeader: 'Address',
      cityHeader: 'City',
      propertyStatusHeader: 'Property Status',
      toursStatusTooltip: 'Properties with Enabled status will accept tour bookings and those with Disabled or Hidden status will not.',
      numberOfAgentsHeader: 'Number of Agents',
    },

    // Tour Properties Single (tour-properties/single/Index.vue)
    propertiesSingle: {
      generalSettings: 'General Settings',
      propertyDisabledBanner: 'Please ensure your property is enabled in Property Management to activate tour bookings.',
      noActiveAgentsBanner: 'Please set assigned agents status to "Active" in order to add a tour for the selected property.',
      property: 'Property',
      address: 'Address',
      previewAvailability: 'Preview Availability',
      previewAvailabilityTooltip: 'Click to preview the availability calendar for this property.',
      toursStatusTooltip: 'Tour properties with Enabled status will accept tour bookings and those with Disabled status will not. To enable Tours for this property, please contact Rentsync Support.',
      noAgentsAssigned: 'No agents assigned',
      includeVirtualTours: 'Include Virtual Tours',
      propertyTimezone: 'Property Timezone',
      usePropertyTimezone: 'Use Property Timezone',
      usePropertyTimezoneTooltip: 'Property Timezone can be set on the property details page.',
      tourPropertySaved: 'Tour property settings saved!',
      agentsWithoutAvailabilitySingular: 'Agent is missing availability settings for this property: {names}.',
      agentsWithoutAvailabilityPlural: 'Agents are missing availability settings for this property: {names}.',
    },

    // Default Property Settings (tour-properties/default-settings/Index.vue)
    defaultSettings: {
      tourPropertySettingsSaved: 'Tour property settings saved!',
      defaultSettingsTitle: 'Default Settings',
      applyDefaultSettings: 'Apply Default Settings',
      applyDefaultSettingsTooltip: 'Clicking this button will apply the current Tour Settings to all tour properties. Only settings that have been saved will be applied.',
      applyDefaultSettingsConfirm: 'Are you sure you would like to apply default settings? This will affect {count} properties. The changes will apply to all future tours booked.',
      cancel: 'Cancel',
      apply: 'Apply',
      defaultSettingsApplied: 'Property default settings applied!',
    },

    // Super Admin Settings (super-admin-settings/Index.vue)
    superAdmin: {
      optionalTourStatus: 'Optional Tour Status (SuperAdmin Only)',
      statusWarning: 'Please note that these settings are not reversible. Once the status toggles are turned ON, you will be unable to turn them back OFF.',
      leasedStatus: 'Leased Status',
      leasedStatusDescription: 'Use this tour status when a prospect has signed a lease.',
      declinedStatus: 'Declined Status',
      declinedStatusDescription: 'Use this tour status when a prospect is not eligible for a lease.',
      unitAssociationTitle: 'ASSOCIATING UNITS WITH TOURS (SuperAdmin Only)',
      unitAssociationDescription: 'Please note that these settings are not reversible. This setting will enable every tour request to be associated with a specific unit.',
    },

    // Tour Forms (tour-forms/Index.vue)
    forms: {
      buildFormTab: 'Build Form',
      tourResponsesTab: 'Tour Responses',
      tourResponseSettingsTab: 'Tour Response Settings',
      contentBlocks: 'Content Blocks',
      searchContentBlocks: 'Search Content Blocks',
      searchContentBlocksPlaceholder: 'Search Content Blocks...',
      customizingForm: 'Customizing a Form',
      dragFieldInstruction: 'Drag a field from the left-hand side to add it to your form.',
      dragDropInstruction: 'Drag & drop blocks from left panel',
      lmatBuildFormInfo1: 'You have language options available. Please add translations to your tour forms to display them on the tours booking widget used on your connected website(s).',
      lmatBuildFormInfo2: 'Please note that if translations are not provided, the form on the booking widget will display the corresponding English values.',
      lmatWhileEditingNote: 'While editing non-English content, some editing features may be hidden / disabled.',
      lmatTourResponsesInfo1: 'We now offer multiple language support for Tour Responses; default content is provided for English and French.',
      lmatTourResponsesInfo2: 'If the prospect chooses English on the Tours Booking Widget, English responses will be sent to prospects (default setting).',
      lmatTourResponsesInfo3: 'If the prospect chooses French on the Tours Booking Widget, French responses will be sent to prospects.',
      lmatTourResponsesInfo4: 'If the prospect chooses a language other than English and French, and translations are provided for that language, the respective translated tour responses will be sent out, otherwise it will default to the English tour responses.',
      lmatTourResponsesInfo5: 'When \'Include form fields in auto-response to prospect\' is toggled on, and if translations are provided under Tours > Settings > Form Settings > Build Form, translated content will be sent to prospects, else it will default to the English content.',
      lmatTextMessageInfo1: 'Currently, text notifications are supported only for English and French languages.',
      lmatTextMessageInfo2: 'If prospects use the French language on the Tours Booking widget, text notifications will be sent out in French.',
      lmatTextMessageInfo3: 'For all other languages, the text notifications will be sent out in English.',
      copyFromEnglishBannerMsg: 'When first setting up your translation forms, you can start with a copy of English fields. If there are existing fields, this will replace them with a copy of English fields.',
      copyFromEnglish: 'Copy from English',
      copied: 'Copied!',
      addTranslationsBannerTitle: 'Add Translations',
      addTranslationsBannerMsg: 'You have websites with language options available. Please add translations to your tour request forms to display them on the tours widget used on your connected website(s).',
      tourRequested: 'Tour Requested',
      tourConfirmed: 'Tour Confirmed',
      tourCancelled: 'Tour Cancelled',
      tourDeclined: 'Tour Declined',
      reminder: 'Reminder',
      prospectCheckIn: 'Prospect Check-In',
      followUp: 'Follow Up',
      tourRequestSubject: 'Tour Request Subject',
      autoResponseMessage: 'Auto-Response Message',
      tourConfirmedSubject: 'Tour Confirmed Subject',
      tourConfirmedMessage: 'Tour Confirmed Message',
      tourCancelledSubject: 'Tour Cancelled Subject',
      tourCancelledMessage: 'Tour Cancelled Message',
      tourDeclinedSubject: 'Tour Declined Subject',
      tourDeclinedMessage: 'Tour Declined Message',
      reminderSubject: 'Reminder Subject',
      reminderMessage: 'Reminder Message',
      checkInSubject: 'Check-In Subject',
      checkInMessage: 'Check-In Message',
      followUpSubject: 'Follow Up Subject',
      followUpMessage: 'Follow Up Message',
      previewEmail: 'Preview Email',
      includeFormFields: 'Include form fields in auto-response to prospect.',
      includeFormFieldsTooltip: 'Selecting the checkbox will display all the form fields (included on the widget) in the email notification that will be sent to prospects.',
      includeRequestFormDetails: 'Include Request Form Details like move-in date and bed count in Auto Response',
      sendTimingHours: 'Send Timing (# of hours before tour booking)',
      sendTimingMinutes: 'Send Timing (# of minutes before tour booking)',
      sendTimingMinutesDesc: 'Specify the number of minutes before an in-person tour that the check-in notification should be sent.',
      checkInExpiration: 'Check-In Expiration (# of minutes after tour booking begins)',
      checkInExpirationDesc: 'Specify the number of minutes after a tour starts that prospects can check in.',
      inHours: 'In Hours',
      inMinutes: 'In Minutes',
      tourRequestedBannerMsg: 'An email confirming submission of form will be sent immediately to the prospect requesting the tour. Subject and message support',
      tourConfirmedBannerMsg: 'An email confirming the tour booking will be sent immediately to the prospect confirming the tour. Subject and message support',
      tourCancelledBannerMsg: 'An email will be sent immediately to the prospect if the tour is cancelled. Subject and message support',
      tourDeclinedBannerMsg: 'An email response will be sent out to prospects when the tour status is changed to "Declined"',
      reminderBannerMsg: 'An email will be sent to the prospect as a reminder for their upcoming tour booking. Subject and message support',
      checkInBannerMsg: 'An email will be sent to the prospect requiring them to check-in to in-person tours when they arrive. Subject and message support',
      followUpBannerMsg: 'An email response will be sent out to prospects when the tour status is changed to "Completed". Subject and message support',
      interpolation: 'interpolation.',
      interpolationError: 'Some interpolation field(s) are not supported. Please refer to the \'interpolation\' link in the banner above for accurate information.',
      responseRequiredError: 'Subject and message are required when this response is enabled.',
      requiredError: 'Subject and message are required.',
      tourEmailPreviewTitle: 'Tour Request Email Preview',
      previewUnsavedChangesBanner: 'Changes must be saved to be reflected on the actual emails.',
      previewSubjectLabel: 'Subject:',
      previewBodyLabel: 'Body:',
      english: 'English',
      emailSettings: 'Email Settings',
      emailSettingsBannerMsg: 'Please add From Name and Email for all your Tour Responses and Reminders here.',
      fromName: '"From" Name',
      fromEmail: '"From" Email',
      fromEmailWarning: 'Please create a support ticket for managing custom \'From\' email settings.',
      textMessageSettings: 'Text Message Settings',
      allowTextNotifications: 'Allow text notifications for prospects',
      textNotificationDescription: 'Text notifications will be sent out when Tours are confirmed or cancelled, or as a reminder 1 hour before the tour date and time. Text notifications will be sent to prospects once they have opted in through the form on the booking widget.',
      textNotificationInterpolation: 'Text notification interpolation fields',
      textNotificationInterpolationDesc: 'Select all fields that you would like to be interpolated in the text notification for confirmation, cancellation, and reminders to prospects.',
      agentName: 'Agent Name',
      agentEmail: 'Agent Email',
      propertyPhoneNumber: 'Property Phone Number',
      textNotificationPreview: 'Text Notification Content Preview',
    },

    // Ad Tracking Settings (super-admin-settings/AdTrackingSettings.vue)
    adTracking: {
      bulkEditAccountId: 'Bulk Edit Account ID',
      bulkEditBannerMsg: 'Bulk editing the Account ID will affect all websites.',
      accountId: 'Account ID',
      googleAdsAccountPlaceholder: 'Google Ads Account',
      enterEventId: 'Enter Event ID',
      enterPixelId: 'Enter Pixel Id',
      enterEventType: 'Enter Event Type',
    },

    // Tour Property Settings card (RsPartialToursPropertySettings.vue)
    propertySettings: {
      learnMoreTitle: 'Learn More - Tour Settings',
      learnMoreAvailabilityTitle: 'Availability',
      learnMoreAvailabilityDesc: 'The hours an agent is available to accept tours per day. This is set on a per-agent basis in agent settings.',
      learnMoreAvailabilityExample: 'Example: 11:00am - 3:00pm',
      learnMoreTimeslotTitle: 'Timeslot Increment',
      learnMoreTimeslotDesc: 'Prospects will be able to see available timeslots on the Tours booking widget based on the increment selected (in minutes).',
      learnMoreTimeslotExample: 'Example: 30 Minutes',
      learnMoreDurationTitle: 'Tour Duration',
      learnMoreDurationDesc: 'The amount of time (in minutes) that a tour takes to complete.',
      learnMoreMinimumNoticeTitle: 'Minimum Notice',
      learnMoreMinimumNoticeDesc: 'The minimum time (in hours) between the incoming request and the requested Tour time.',
      learnMoreMinimumNoticeExample: 'Example: 2 Hours',
      learnMoreBufferTitle: 'Buffer',
      learnMoreBufferDesc: 'Additional time (in minutes) before or after a tour or at the beginning or end of agent\'s daily availability. Buffers can be used if agents need time to prepare for or travel to the next tour.',
      learnMoreBufferExampleTitle: 'Example:',
      learnMoreBufferExample1Bold: '15 Minutes',
      learnMoreBufferExample1: 'Buffer before & after tours.',
      learnMoreBufferExample2: 'Buffer at the beginning & before the end of agent\'s daily availability.',
      learnMoreScheduleTitle: 'Tour Scheduling Example',
      learnMoreScheduleDesc: 'A prospect wants to book a tour with a property for today.',
      learnMoreCurrentTime: 'Current Time: 11:00am',
      tourSettingsTitle: 'Tour Settings',
      learnMore: 'Learn More',
      defaultTourDuration: 'Default Tour Duration',
      customDuration: 'Custom Duration (in mins)',
      timeslotIncrement: 'Timeslot Increment',
      customIncrement: 'Custom Increment (in mins)',
      minimumNotice: 'Minimum Notice',
      customMinimumNotice: 'Custom Minimum Notice (in hours)',
      bufferBeforeTour: 'Buffer Before Tour',
      bufferBeforeDescPart1: 'Include buffer at the',
      bufferBeforeDescBold: 'beginning',
      bufferBeforeDescPart2: 'of agent\'s daily availability',
      bufferAfterTour: 'Buffer After Tour',
      bufferAfterDescPart1: 'Include buffer',
      bufferAfterDescBold: 'before the end',
      bufferAfterDescPart2: 'of agent\'s availability',
      bookingWindow: 'Booking Window',
      bookingWindowTooltip: 'Enter the maximum number of upcoming days you want the tour calendar to display as available for booking (e.g.entering 7 days means only the next seven days, including today, will be shown).',
      autoApprove: 'Auto Approve Tour Requests',
      widgetTitle: 'Widget Title',
      widgetTitlePropertyOnly: 'Show Property Name Only',
      widgetTitleAddressOnly: 'Show Address Only',
      widgetTitleBoth: 'Show Both',
    },

    // Email Settings card (RsPartialToursEmailSettings.vue)
    propertyEmailSettings: {
      emailSettingsTitle: 'Email Settings',
      tourEmailNotifications: 'Tour Email Notifications',
      tourEmailNotificationsTooltip: 'Confirmations and cancellations will be sent to the agent assigned to the tour or the custom email address based on your selection here',
      emailLabel: 'Email',
      replyToLabel: '\'Reply To\'',
      replyToEmailBold: 'Email',
      agentEmailOption: 'Agent Email',
      clientEmail: 'Client Email',
      propertyEmail: 'Property Email',
      customOption: 'Custom',
      noneFromEmail: 'None (From Email Address)',
      noneClientOrFromEmail: 'None (Client or From Email Address)',
      noneDefaultTooltip: 'The configured \'From\' email address under Form Settings will be used.',
      nonePropertyTooltip: 'This will use the client\'s Reply-To email address setting.',
      toggleBcc: 'Toggle BCC Options On/Off',
      bccRequired: 'Input is required for at least one of the below fields.',
      bccTracking: 'BCC for Tracking',
      bccTrackingTooltip: 'Please input email addresses for tracking confirmed tours.',
      bccManagers: 'BCC for Managers',
      bccManagersTooltip: 'An additional copy of every tour request will be sent to this email addresses with the ability to confirm or cancel tours.',
      customPropertyUrl: 'Custom Property URL',
      customPropertyUrlTooltip: 'This URL can be referenced using the [[tour_property_custom_url]] interpolation tag in prospect messages.',
      customEmailLabel: 'Custom Email',
      emailTypeAgent: 'Agent',
      emailTypeCustom: 'Custom',
    },

    // Tour Agent Availability Preview modal (RsModalTourAgentAvailabilityPreview.vue)
    // Default Agent Settings Modal (RsModalDefaultAgentSettings.vue)
    defaultAgentSettings: {
      banner: 'These settings will automatically apply when new tour agents are added to your Rentsync account and given permissions for the Tours module. Settings can be customized in Agent Settings for individual tour agents.',
      tourSettingsCard: 'Tour Settings',
      defaultTimezone: 'Default Timezone',
      setAgentAvailabilityBanner: 'Set agent availability by property in the individual agents setting page',
      settingsSaved: 'Default agent settings have been saved successfully.',
    },

    agentAvailabilityPreview: {
      modalTitle: 'Tour Agent Availability',
      propertyLabel: 'Property',
      timesShownInTimezone: 'Times shown in property timezone',
      missingAvailabilitySingular: 'Agent is missing availability settings for this property: {names}',
      missingAvailabilityPlural: 'Agents are missing availability settings for this property: {names}',
      currentProperty: 'Current Property',
      failedToLoad: 'Failed to load agent availability',
      agentAvailabilityTitle: 'Agent Availability',
      agentAvailabilityMessage: '{agentName}\'s availability',
      unknownAgent: 'Unknown Agent',
      calendarToday: 'Today',
      calendarDay: 'Day',
      calendarWeek: 'Week',
      calendarMonth: 'Month',
    },
  },
} as const;

export const toursSettingsFr = {
  toursSettings: {
    // Common
    common: {
      changesSaved: 'Modifications enregistrées !',
      changesSavedMessage: 'Les modifications apportées au formulaire ont été enregistrées avec succès.',
      savingForm: 'Enregistrement du formulaire',
      saving: 'Enregistrement...',
      errorsBeforeSaving: 'Vous avez des erreurs ! Veuillez les corriger avant d\'enregistrer.',
      unsavedChanges: 'Vous avez des modifications non enregistrées !',
      noPermission: 'Vous n\'avez pas la permission d\'apporter des modifications',
      somethingWentWrong: 'Une erreur s\'est produite',
      on: 'Activé',
      off: 'Désactivé',
      enabled: 'Activé',
      disabled: 'Désactivé',
      active: 'Actif',
      inactive: 'Inactif',
      select: 'Sélectionner',
      all: '[Tous]',
      notApplicable: 'S.O.',
      properties: 'Propriétés',
      status: 'Statut',
      remove: 'Supprimer',
      confirm: 'Confirmer',
      example: 'Exemple',
      message: 'Message',
      gotIt: 'Compris',
      days: 'jours',
      custom: 'Personnalisé',
      daysAvailable: 'Jours disponibles',
      sundayInitial: 'D',
      mondayInitial: 'L',
      tuesdayInitial: 'M',
      wednesdayInitial: 'M',
      thursdayInitial: 'J',
      fridayInitial: 'V',
      saturdayInitial: 'S',
      sundayName: 'Dimanche',
      mondayName: 'Lundi',
      tuesdayName: 'Mardi',
      wednesdayName: 'Mercredi',
      thursdayName: 'Jeudi',
      fridayName: 'Vendredi',
      saturdayName: 'Samedi',
      to: 'à',
    },

    // Tour General Settings (tour-general/Index.vue)
    general: {
      brandSettings: 'Paramètres de la marque',
      logo: 'Logo',
      logoTooltip: 'Ce logo est importé de votre profil d\'entreprise. Si vous souhaitez apporter des modifications, veuillez contacter votre gestionnaire de compte Rentsync.',
      backgroundColour: 'Couleur d\'arrière-plan',
      brandColourPreview: 'Aperçu de la couleur de la marque',
      brandColourBanner: 'Veuillez vous assurer que le texte est clairement visible sur la couleur de marque choisie dans la zone de test ci-dessous. La couleur de la marque sera utilisée dans tout le widget de réservation de visites.',
      testTitle: 'Votre titre de test ici.',
      customMessageTitle: 'Message personnalisé sur le widget de visites',
      learnMoreTranslations: 'En savoir plus sur les traductions',
      learnMoreTranslationsTitle: 'En savoir plus - Traductions',
      translationsInfo1: 'Vous avez des options de langue disponibles. Veuillez ajouter des traductions à votre message personnalisé pour les afficher sur le widget de réservation de visites utilisé sur votre ou vos sites web connectés.',
      translationsInfo2: 'Veuillez noter que si les traductions ne sont pas fournies, le message personnalisé sur le widget de réservation affichera les valeurs anglaises correspondantes.',
      customMessageDescription: 'Ce message apparaîtra en haut de chaque page du widget de réservation de visites - Date, Heure et Formulaire.',
      interpolationSupport: 'Le message personnalisé du widget de visites prend en charge',
      interpolation: 'l\'interpolation.',
      customMessageToggleDescription: 'Si vous souhaitez ajouter un message personnalisé sur votre widget de réservation de visites pour les prospects, veuillez activer ce paramètre et ajouter votre message personnalisé ci-dessous.',
      defaultCustomMessage: 'Réservez votre visite dès aujourd\'hui. Nous avons hâte de vous accueillir.',
      setMessageBottom: 'Définir le message en bas du widget de réservation de visites',
      setMessageBottomTooltip: 'Par défaut, le message personnalisé apparaît au-dessus du formulaire. Activez cette option pour afficher le message personnalisé sous le formulaire à la place.',
      customMessageExampleTitle: 'Exemple de message personnalisé du widget de visites',
      tourGeneralTranslationsHeader: 'Traductions des paramètres généraux des visites',
      // Color Picker Modal
      colorPickerTitle: 'Couleur d\'arrière-plan',
      colorPickerHex: 'Hex',
      colorPickerSave: 'Enregistrer',
      // Interpolation Modal
      interpolationTitle: 'Interpolation',
      interpolationDescription: 'Cela fait référence à l\'ajout de données dynamiques de votre formulaire dans un texte autrement statique.',
      interpolationKeyInstruction: 'Toute interpolation se fait en ajoutant une « clé » entre double crochets dans votre texte.',
      interpolationExample: 'Exemple : « Bonjour, [[full_name]] »',
      interpolationFormNameInfo: 'La clé peut être le « Nom du formulaire » de n\'importe quel champ de votre formulaire (situé sous « Afficher les options avancées »). Si votre nom de formulaire indique « [auto] », vous pouvez en ajouter un vous-même ou enregistrer le formulaire pour en générer un d\'abord.',
      interpolationPredefinedKeys: 'Les clés d\'interpolation peuvent également être l\'une des clés prédéfinies suivantes, qui font référence aux détails d\'une propriété ou d\'une ville si une est sélectionnée d\'une manière ou d\'une autre.',
      interpolationTour: 'Visite :',
      interpolationProperty: 'Propriété :',
      interpolationCity: 'Ville :',
      interpolationProspect: 'Prospect :',
    },

    // Agent Settings List (agent-settings/Index.vue)
    agentList: {
      defaultAgentSettings: 'Paramètres d\'agent par défaut',
      removeFilters: 'Supprimer',
      filters: 'Filtres',
      filter: 'Filtre',
      tourAgentHeader: 'Agent de visite',
      emailAddressHeader: 'Adresse courriel',
      agentStatusHeader: 'Statut de l\'agent',
      agentStatusHeaderTooltip: 'Le statut de l\'agent indique si un agent de visite accepte des réservations de visites à ce moment ou non.',
      availabilityHeader: 'Disponibilité',
      numberOfPropertiesHeader: '# De propriétés',
    },

    // Agent Settings Single (agent-settings/single/Index.vue)
    agentSingle: {
      tourAgentProfile: 'Profil de l\'agent de visite',
      agentStatusBanner: 'Si le statut de l\'agent est défini sur « Inactif » et que la propriété n\'a pas d\'autre agent de visite actif assigné, de nouvelles visites ne peuvent pas être demandées pour cette propriété.',
      firstName: 'Prénom',
      lastName: 'Nom de famille',
      email: 'Courriel',
      agentStatus: 'Statut de l\'agent',
      agentStatusTooltip: 'Le statut indique si un agent de visite effectue des réservations de visites à ce moment ou non.',
      calendarSyncSettings: 'Paramètres de synchronisation du calendrier',
      removeCalendars: 'Supprimer les calendriers',
      reSyncCalendar: 'Resynchroniser le calendrier',
      reSyncCalendarTooltip: 'Cliquez pour voir une liste mise à jour des calendriers connectés',
      calendarDisconnected: 'Votre calendrier externe lié a été déconnecté. Pour vous reconnecter, cliquez sur le bouton ci-dessous.',
      connectCalendars: 'Connectez vos calendriers existants depuis Google, Apple, Microsoft ou Exchange. Vous pouvez choisir de partager vos réservations avec votre calendrier quotidien.',
      syncMyCalendar: 'Synchroniser mon calendrier',
      emailedEventsWarning: 'Les événements envoyés par courriel sont un calendrier en lecture seule. Les visites créées sur le calendrier Rentsync ne seront pas ajoutées au calendrier des événements par courriel.',
      bulkCalendarSettings: 'Paramètres de calendrier en masse',
      custom: 'Personnalisé',
      customTooltip: 'Utilisez les interrupteurs du tableau pour sélectionner les paramètres de synchronisation du calendrier individuellement.',
      disableAllCalendars: 'Désactiver tous les calendriers',
      calendarSyncing: 'Le calendrier est en cours de synchronisation',
      calendarPending: 'La synchronisation du calendrier est en attente ou déconnectée',
      lastSynced: 'dernière synchronisation',
      calendarSyncStatus: 'Statut de synchronisation du calendrier',
      agentSettingsInternal: 'Paramètres de l\'agent (Usage interne seulement)',
      timezoneInternalBanner: 'Le paramétrage du fuseau horaire ci-dessous vous permettra de visualiser la disponibilité et les plages horaires de l\'agent dans le fuseau horaire sélectionné. Le système convertira et enregistrera les modifications effectuées dans le fuseau horaire par défaut {timezone}.',
      timezoneInternal: 'Fuseau horaire (Usage interne seulement)',
      availability: 'Disponibilité',
      setPerProperty: 'Définir par propriété',
      setPerPropertyTooltip: 'Cette option n\'est disponible que pour les agents assignés à plusieurs propriétés.',
      allTimesShownIn: 'Tous les horaires affichés dans le fuseau horaire {timezone}',
      availabilityDescription: 'Ce paramètre permettra aux prospects de réserver des visites dans la plage horaire définie. Tous les calendriers intégrés seront affectés.',
      timezoneUpdateBanner: 'Il y a eu une mise à jour du fuseau horaire de l\'entreprise. Réenregistrez vos paramètres pour synchroniser vos disponibilités au fuseau horaire {timezone}.',
      propertiesWithoutAvailability: 'Avertissement : Vous avez des propriétés sans disponibilité définie. Veuillez vous assurer que votre disponibilité est correctement attribuée.',
      addNewInterval: 'Ajouter un nouvel intervalle',
      copyTime: 'Copier l\'heure',
      copied: 'Copié',
      pasteTime: 'Coller l\'heure',
      conflictingIntervals: 'Intervalles conflictuels détectés pour la même propriété. Ajustez les heures ou supprimez les attributions de propriété avant d\'enregistrer.',
      calendars: 'Calendriers',
      syncingOptions: 'Options de synchronisation',
      syncCalendars: 'Synchroniser le(s) calendrier(s)',
      importEventTitles: 'Importer les titres d\'événements',
      importEventTitlesTooltip: 'Activez pour importer les titres d\'événements de votre calendrier ou ils s\'afficheront comme un bloc de temps.',
      importNonBusyEvents: 'Importer les événements non occupés',
      importNonBusyEventsTooltip: 'Désactivez pour arrêter d\'importer les événements de votre calendrier qui ne sont pas marqués comme « occupé ».',
      deleteCalendarTitle: 'Supprimer le calendrier',
      deleteCalendarMessage: 'La suppression du calendrier désynchronisera tous vos calendriers de Rentsync Tours. Êtes-vous sûr de vouloir continuer ?',
      deleteCalendarNote: 'Veuillez appuyer sur « Enregistrer » sur la page après avoir quitté la fenêtre pour appliquer les modifications.',
      disableCalendarTitle: 'Désactiver le calendrier de Rentsync Tours',
      disableCalendarMessage: 'La désactivation du calendrier empêchera les événements futurs sur le(s) calendrier(s) sélectionné(s) de se synchroniser avec le calendrier Rentsync Tours. Êtes-vous sûr de vouloir continuer ?',
      propertiesWithoutAvailabilityTitle: 'Propriétés sans disponibilité',
      propertiesWithoutAvailabilityMessage: 'Vous avez des propriétés sans disponibilité définie. Cela signifie que les visites ne peuvent pas être réservées pour ces propriétés. Êtes-vous sûr de vouloir continuer à enregistrer ?',
      agentSettingsSaved: 'Les modifications apportées aux paramètres de l\'agent ont été enregistrées avec succès.',
      conflictingAvailabilities: 'Disponibilités conflictuelles',
      resolveOverlapping: 'Résolvez les intervalles qui se chevauchent pour la même propriété avant d\'enregistrer.',
      sameAvailabilityEveryday: 'Même disponibilité tous les jours',
      differentAvailabilityEveryday: 'Disponibilité différente chaque jour',
    },

    // Tour Properties List (tour-properties/Index.vue)
    propertiesList: {
      search: 'Rechercher',
      cities: 'Villes',
      tourAgents: 'Agents de visite',
      toursStatus: 'Statut des visites',
      bulkEditProperties: 'Modifier les propriétés en masse',
      defaultPropertySettings: 'Paramètres de propriété par défaut',
      missingAgentAvailability: 'Disponibilité d\'agent manquante pour un ou plusieurs agents',
      propertiesSaved: 'Les modifications apportées aux propriétés ont été enregistrées avec succès.',
      enableTourStatus: 'Activer le statut de visite',
      disableTourStatus: 'Désactiver le statut de visite',
      bulkActionEnable: 'Activer',
      bulkActionDisable: 'Désactiver',
      bulkActionSuffix: 'statut de visite pour',
      selectedProperties: 'Sélectionnés :',
      propertiesSuffix: 'propriétés',
      addressHeader: 'Adresse',
      cityHeader: 'Ville',
      propertyStatusHeader: 'Statut de la propriété',
      toursStatusTooltip: 'Les propriétés avec le statut Activé accepteront les réservations de visites, tandis que celles avec le statut Désactivé ou Masqué n\'en accepteront pas.',
      numberOfAgentsHeader: 'Nombre d\'agents',
    },

    // Tour Properties Single (tour-properties/single/Index.vue)
    propertiesSingle: {
      generalSettings: 'Paramètres généraux',
      propertyDisabledBanner: 'Veuillez vous assurer que votre propriété est activée dans la gestion des propriétés pour activer les réservations de visites.',
      noActiveAgentsBanner: 'Veuillez définir le statut des agents assignés sur « Actif » afin d\'ajouter une visite pour la propriété sélectionnée.',
      property: 'Propriété',
      address: 'Adresse',
      previewAvailability: 'Aperçu de la disponibilité',
      previewAvailabilityTooltip: 'Cliquez pour prévisualiser le calendrier de disponibilité pour cette propriété.',
      toursStatusTooltip: 'Les propriétés de visite avec le statut Activé accepteront les réservations de visites et celles avec le statut Désactivé ne les accepteront pas. Pour activer les visites pour cette propriété, veuillez contacter le support Rentsync.',
      noAgentsAssigned: 'Aucun agent assigné',
      includeVirtualTours: 'Inclure les visites virtuelles',
      propertyTimezone: 'Fuseau horaire de la propriété',
      usePropertyTimezone: 'Utiliser le fuseau horaire de la propriété',
      usePropertyTimezoneTooltip: 'Le fuseau horaire de la propriété peut être défini sur la page de détails de la propriété.',
      tourPropertySaved: 'Paramètres de propriété de visite enregistrés !',
      agentsWithoutAvailabilitySingular: 'L\'agent manque de disponibilités pour cette propriété : {names}.',
      agentsWithoutAvailabilityPlural: 'Les agents manquent de disponibilités pour cette propriété : {names}.',
    },

    // Default Property Settings (tour-properties/default-settings/Index.vue)
    defaultSettings: {
      tourPropertySettingsSaved: 'Paramètres de propriété de visite enregistrés !',
      defaultSettingsTitle: 'Paramètres par défaut',
      applyDefaultSettings: 'Appliquer les paramètres par défaut',
      applyDefaultSettingsTooltip: 'En cliquant sur ce bouton, vous appliquerez les paramètres de visite actuels à toutes les propriétés de visite. Seuls les paramètres qui ont été enregistrés seront appliqués.',
      applyDefaultSettingsConfirm: 'Êtes-vous sûr de vouloir appliquer les paramètres par défaut ? Cela affectera {count} propriétés. Les modifications s\'appliqueront à toutes les futures visites réservées.',
      cancel: 'Annuler',
      apply: 'Appliquer',
      defaultSettingsApplied: 'Paramètres par défaut de la propriété appliqués !',
    },

    // Super Admin Settings (super-admin-settings/Index.vue)
    superAdmin: {
      optionalTourStatus: 'Statut de visite optionnel (SuperAdmin seulement)',
      statusWarning: 'Veuillez noter que ces paramètres ne sont pas réversibles. Une fois les interrupteurs de statut activés, vous ne pourrez plus les désactiver.',
      leasedStatus: 'Statut loué',
      leasedStatusDescription: 'Utilisez ce statut de visite lorsqu\'un prospect a signé un bail.',
      declinedStatus: 'Statut refusé',
      declinedStatusDescription: 'Utilisez ce statut de visite lorsqu\'un prospect n\'est pas éligible pour un bail.',
      unitAssociationTitle: 'ASSOCIATION DES UNITÉS AUX VISITES (SuperAdmin seulement)',
      unitAssociationDescription: 'Veuillez noter que ces paramètres ne sont pas réversibles. Ce paramètre permettra d\'associer chaque demande de visite à une unité spécifique.',
    },

    // Tour Forms (tour-forms/Index.vue)
    forms: {
      buildFormTab: 'Construire un formulaire',
      tourResponsesTab: 'Réponses aux visites',
      tourResponseSettingsTab: 'Paramètres de réponse aux visites',
      contentBlocks: 'Blocs de contenu',
      searchContentBlocks: 'Rechercher des blocs de contenu',
      searchContentBlocksPlaceholder: 'Rechercher des blocs de contenu...',
      customizingForm: 'Personnalisation d\'un formulaire',
      dragFieldInstruction: 'Faites glisser un champ du côté gauche pour l\'ajouter à votre formulaire.',
      dragDropInstruction: 'Glissez-déposez les blocs du panneau de gauche',
      lmatBuildFormInfo1: 'Vous avez des options de langue disponibles. Veuillez ajouter des traductions à vos formulaires de visite pour les afficher sur le widget de réservation de visites utilisé sur votre ou vos sites web connectés.',
      lmatBuildFormInfo2: 'Veuillez noter que si les traductions ne sont pas fournies, le formulaire sur le widget de réservation affichera les valeurs anglaises correspondantes.',
      lmatWhileEditingNote: 'Lors de la modification de contenu non anglais, certaines fonctionnalités d\'édition peuvent être masquées / désactivées.',
      lmatTourResponsesInfo1: 'Nous offrons maintenant la prise en charge de plusieurs langues pour les réponses aux visites ; le contenu par défaut est fourni en anglais et en français.',
      lmatTourResponsesInfo2: 'Si le prospect choisit l\'anglais sur le widget de réservation de visites, les réponses en anglais seront envoyées aux prospects (paramètre par défaut).',
      lmatTourResponsesInfo3: 'Si le prospect choisit le français sur le widget de réservation de visites, les réponses en français seront envoyées aux prospects.',
      lmatTourResponsesInfo4: 'Si le prospect choisit une langue autre que l\'anglais et le français, et que des traductions sont fournies pour cette langue, les réponses traduites correspondantes seront envoyées, sinon les réponses en anglais seront envoyées par défaut.',
      lmatTourResponsesInfo5: 'Lorsque \'Inclure les champs du formulaire dans la réponse automatique au prospect\' est activé, et si des traductions sont fournies sous Visites > Paramètres > Paramètres du formulaire > Construire un formulaire, le contenu traduit sera envoyé aux prospects, sinon le contenu anglais sera envoyé par défaut.',
      lmatTextMessageInfo1: 'Actuellement, les notifications par message texte sont uniquement disponibles en anglais et en français.',
      lmatTextMessageInfo2: 'Si les prospects utilisent le français sur le widget de réservation de visites, les notifications par message texte seront envoyées en français.',
      lmatTextMessageInfo3: 'Pour toutes les autres langues, les notifications par message texte seront envoyées en anglais.',
      copyFromEnglishBannerMsg: 'Lors de la première configuration de vos formulaires de traduction, vous pouvez commencer avec une copie des champs anglais. S\'il existe déjà des champs, ceux-ci seront remplacés par une copie des champs anglais.',
      copyFromEnglish: 'Copier depuis l\'anglais',
      copied: 'Copié !',
      addTranslationsBannerTitle: 'Ajouter des traductions',
      addTranslationsBannerMsg: 'Vous avez des sites web avec des options de langue disponibles. Veuillez ajouter des traductions à vos formulaires de demande de visite pour les afficher sur le widget de visites utilisé sur votre ou vos sites web connectés.',
      tourRequested: 'Visite demandée',
      tourConfirmed: 'Visite confirmée',
      tourCancelled: 'Visite annulée',
      tourDeclined: 'Visite refusée',
      reminder: 'Rappel',
      prospectCheckIn: 'Enregistrement du prospect',
      followUp: 'Suivi',
      tourRequestSubject: 'Objet de la demande de visite',
      autoResponseMessage: 'Message de réponse automatique',
      tourConfirmedSubject: 'Objet de confirmation de visite',
      tourConfirmedMessage: 'Message de confirmation de visite',
      tourCancelledSubject: 'Objet d\'annulation de visite',
      tourCancelledMessage: 'Message d\'annulation de visite',
      tourDeclinedSubject: 'Objet de refus de visite',
      tourDeclinedMessage: 'Message de refus de visite',
      reminderSubject: 'Objet du rappel',
      reminderMessage: 'Message de rappel',
      checkInSubject: 'Objet de l\'enregistrement',
      checkInMessage: 'Message d\'enregistrement',
      followUpSubject: 'Objet du suivi',
      followUpMessage: 'Message de suivi',
      previewEmail: 'Aperçu du courriel',
      includeFormFields: 'Inclure les champs du formulaire dans la réponse automatique au prospect.',
      includeFormFieldsTooltip: 'Sélectionner la case à cocher affichera tous les champs du formulaire (inclus dans le widget) dans la notification par courriel qui sera envoyée aux prospects.',
      includeRequestFormDetails: 'Inclure les détails du formulaire de demande comme la date d\'emménagement et le nombre de chambres dans la réponse automatique',
      sendTimingHours: 'Délai d\'envoi (nombre d\'heures avant la réservation de visite)',
      sendTimingMinutes: 'Délai d\'envoi (nombre de minutes avant la réservation de visite)',
      sendTimingMinutesDesc: 'Indiquez combien de minutes avant une visite en personne la notification d\'enregistrement doit être envoyée.',
      checkInExpiration: 'Expiration de l\'enregistrement (nombre de minutes après le début de la visite)',
      checkInExpirationDesc: 'Indiquez le nombre de minutes après le début d\'une visite pendant lesquelles les prospects peuvent s\'enregistrer.',
      inHours: 'En heures',
      inMinutes: 'En minutes',
      tourRequestedBannerMsg: 'Un courriel confirmant la soumission du formulaire sera envoyé immédiatement au prospect demandant la visite. L\'objet et le message prennent en charge',
      tourConfirmedBannerMsg: 'Un courriel confirmant la réservation de la visite sera envoyé immédiatement au prospect confirmant la visite. L\'objet et le message prennent en charge',
      tourCancelledBannerMsg: 'Un courriel sera envoyé immédiatement au prospect si la visite est annulée. L\'objet et le message prennent en charge',
      tourDeclinedBannerMsg: 'Un courriel de réponse sera envoyé aux prospects lorsque le statut de la visite est changé en "Refusé"',
      reminderBannerMsg: 'Un courriel sera envoyé au prospect comme rappel pour sa prochaine réservation de visite. L\'objet et le message prennent en charge',
      checkInBannerMsg: 'Un courriel sera envoyé au prospect lui demandant de s\'enregistrer pour les visites en personne à son arrivée. L\'objet et le message prennent en charge',
      followUpBannerMsg: 'Un courriel de réponse sera envoyé aux prospects lorsque le statut de la visite est changé en "Terminé". L\'objet et le message prennent en charge',
      interpolation: 'l\'interpolation.',
      interpolationError: 'Certains champs d\'interpolation ne sont pas pris en charge. Veuillez consulter le lien \'interpolation\' dans la bannière ci-dessus pour obtenir des informations précises.',
      responseRequiredError: 'L\'objet et le message sont obligatoires lorsque cette réponse est activée.',
      requiredError: 'L\'objet et le message sont obligatoires.',
      tourEmailPreviewTitle: 'Aperçu du courriel de demande de visite',
      previewUnsavedChangesBanner: 'Les modifications doivent être enregistrées pour être reflétées sur les courriels réels.',
      previewSubjectLabel: 'Objet :',
      previewBodyLabel: 'Corps :',
      english: 'Anglais',
      emailSettings: 'Paramètres de courriel',
      emailSettingsBannerMsg: 'Veuillez ajouter le nom et l\'adresse de courriel de l\'expéditeur pour toutes vos réponses aux visites et vos rappels ici.',
      fromName: '"De" Nom',
      fromEmail: '"De" Courriel',
      fromEmailWarning: 'Veuillez créer un ticket de support pour gérer les paramètres d\'adresse de courriel de l\'expéditeur personnalisés.',
      textMessageSettings: 'Paramètres des messages texte',
      allowTextNotifications: 'Autoriser les notifications par message texte pour les prospects',
      textNotificationDescription: 'Les notifications par message texte seront envoyées lorsque les visites sont confirmées ou annulées, ou comme rappel 1 heure avant la date et l\'heure de la visite. Les notifications par message texte seront envoyées aux prospects une fois qu\'ils auront opté via le formulaire sur le widget de réservation.',
      textNotificationInterpolation: 'Champs d\'interpolation des notifications par message texte',
      textNotificationInterpolationDesc: 'Sélectionnez tous les champs que vous souhaitez interpoler dans la notification par message texte pour les confirmations, annulations et rappels aux prospects.',
      agentName: 'Nom de l\'agent',
      agentEmail: 'Courriel de l\'agent',
      propertyPhoneNumber: 'Numéro de téléphone de la propriété',
      textNotificationPreview: 'Aperçu du contenu de la notification par message texte',
    },

    // Ad Tracking Settings (super-admin-settings/AdTrackingSettings.vue)
    adTracking: {
      bulkEditAccountId: 'Modification en masse de l\'ID de compte',
      bulkEditBannerMsg: 'La modification en masse de l\'ID de compte affectera tous les sites web.',
      accountId: 'ID de compte',
      googleAdsAccountPlaceholder: 'Compte Google Ads',
      enterEventId: 'Entrer l\'ID d\'événement',
      enterPixelId: 'Entrer l\'ID Pixel',
      enterEventType: 'Entrer le type d\'événement',
    },

    // Tour Property Settings card (RsPartialToursPropertySettings.vue)
    propertySettings: {
      learnMoreTitle: 'En savoir plus - Paramètres des visites',
      learnMoreAvailabilityTitle: 'Disponibilité',
      learnMoreAvailabilityDesc: 'Les heures pendant lesquelles un agent est disponible pour accepter des visites par jour. Cela est défini par agent dans les paramètres de l\'agent.',
      learnMoreAvailabilityExample: 'Exemple : 11h00 - 15h00',
      learnMoreTimeslotTitle: 'Incrément de créneau',
      learnMoreTimeslotDesc: 'Les prospects pourront voir les créneaux disponibles sur le widget de réservation de visites en fonction de l\'incrément sélectionné (en minutes).',
      learnMoreTimeslotExample: 'Exemple : 30 minutes',
      learnMoreDurationTitle: 'Durée de la visite',
      learnMoreDurationDesc: 'Le temps (en minutes) nécessaire pour effectuer une visite.',
      learnMoreMinimumNoticeTitle: 'Préavis minimum',
      learnMoreMinimumNoticeDesc: 'Le délai minimum (en heures) entre la demande entrante et l\'heure de visite demandée.',
      learnMoreMinimumNoticeExample: 'Exemple : 2 heures',
      learnMoreBufferTitle: 'Tampon',
      learnMoreBufferDesc: 'Temps supplémentaire (en minutes) avant ou après une visite ou au début ou à la fin de la disponibilité quotidienne de l\'agent. Les tampons peuvent être utilisés si les agents ont besoin de temps pour se préparer ou se déplacer vers la prochaine visite.',
      learnMoreBufferExampleTitle: 'Exemple :',
      learnMoreBufferExample1Bold: '15 minutes',
      learnMoreBufferExample1: 'Tampon avant et après les visites.',
      learnMoreBufferExample2: 'Tampon au début et avant la fin de la disponibilité quotidienne de l\'agent.',
      learnMoreScheduleTitle: 'Exemple de planification de visite',
      learnMoreScheduleDesc: 'Un prospect souhaite réserver une visite avec une propriété pour aujourd\'hui.',
      learnMoreCurrentTime: 'Heure actuelle : 11h00',
      tourSettingsTitle: 'Paramètres des visites',
      learnMore: 'En savoir plus',
      defaultTourDuration: 'Durée de visite par défaut',
      customDuration: 'Durée personnalisée (en min)',
      timeslotIncrement: 'Incrément de créneau',
      customIncrement: 'Incrément personnalisé (en min)',
      minimumNotice: 'Préavis minimum',
      customMinimumNotice: 'Préavis minimum personnalisé (en heures)',
      bufferBeforeTour: 'Tampon avant la visite',
      bufferBeforeDescPart1: 'Inclure le tampon au',
      bufferBeforeDescBold: 'début',
      bufferBeforeDescPart2: 'de la disponibilité quotidienne de l\'agent',
      bufferAfterTour: 'Tampon après la visite',
      bufferAfterDescPart1: 'Inclure le tampon',
      bufferAfterDescBold: 'avant la fin',
      bufferAfterDescPart2: 'de la disponibilité de l\'agent',
      bookingWindow: 'Fenêtre de réservation',
      bookingWindowTooltip: 'Entrez le nombre maximum de jours à venir que vous souhaitez afficher comme disponibles pour la réservation dans le calendrier des visites (par exemple, entrer 7 jours signifie que seuls les sept prochains jours, y compris aujourd\'hui, seront affichés).',
      autoApprove: 'Approbation automatique des demandes de visite',
      widgetTitle: 'Titre du widget',
      widgetTitlePropertyOnly: 'Afficher uniquement le nom de la propriété',
      widgetTitleAddressOnly: 'Afficher uniquement l\'adresse',
      widgetTitleBoth: 'Afficher les deux',
    },

    // Email Settings card (RsPartialToursEmailSettings.vue)
    propertyEmailSettings: {
      emailSettingsTitle: 'Paramètres de courriel',
      tourEmailNotifications: 'Notifications par courriel de visite',
      tourEmailNotificationsTooltip: 'Les confirmations et annulations seront envoyées à l\'agent assigné à la visite ou à l\'adresse courriel personnalisée en fonction de votre sélection ici',
      emailLabel: 'Courriel',
      replyToLabel: '\'Répondre à\'',
      replyToEmailBold: 'Courriel',
      agentEmailOption: 'Courriel de l\'agent',
      clientEmail: 'Courriel du client',
      propertyEmail: 'Courriel de la propriété',
      customOption: 'Personnalisé',
      noneFromEmail: 'Aucun (adresse de courriel de l\'expéditeur)',
      noneClientOrFromEmail: 'Aucun (courriel du client ou de l\'expéditeur)',
      noneDefaultTooltip: 'L\'adresse courriel de l\'expéditeur configurée sous Paramètres du formulaire sera utilisée.',
      nonePropertyTooltip: 'Cela utilisera le paramètre d\'adresse de réponse du client.',
      toggleBcc: 'Activer/Désactiver les options CCI',
      bccRequired: 'Une saisie est requise pour au moins un des champs ci-dessous.',
      bccTracking: 'CCI pour le suivi',
      bccTrackingTooltip: 'Veuillez saisir des adresses courriel pour le suivi des visites confirmées.',
      bccManagers: 'CCI pour les gestionnaires',
      bccManagersTooltip: 'Une copie supplémentaire de chaque demande de visite sera envoyée à ces adresses courriel avec la possibilité de confirmer ou d\'annuler les visites.',
      customPropertyUrl: 'URL personnalisée de la propriété',
      customPropertyUrlTooltip: 'Cette URL peut être référencée en utilisant le tag d\'interpolation [[tour_property_custom_url]] dans les messages aux prospects.',
      customEmailLabel: 'Courriel personnalisé',
      emailTypeAgent: 'Agent',
      emailTypeCustom: 'Personnalisé',
    },

    // Tour Agent Availability Preview modal (RsModalTourAgentAvailabilityPreview.vue)
    // Default Agent Settings Modal (RsModalDefaultAgentSettings.vue)
    defaultAgentSettings: {
      banner: 'Ces paramètres s\'appliqueront automatiquement lorsque de nouveaux agents de visite seront ajoutés à votre compte Rentsync et que des autorisations pour le module Visites leur seront accordées. Les paramètres peuvent être personnalisés dans les paramètres de l\'agent pour les agents de visite individuels.',
      tourSettingsCard: 'Paramètres de visite',
      defaultTimezone: 'Fuseau horaire par défaut',
      setAgentAvailabilityBanner: 'Définir la disponibilité de l\'agent par propriété dans la page des paramètres des agents individuels',
      settingsSaved: 'Les paramètres par défaut de l\'agent ont été enregistrés avec succès.',
    },

    agentAvailabilityPreview: {
      modalTitle: 'Disponibilité des agents de visite',
      propertyLabel: 'Propriété',
      timesShownInTimezone: 'Horaires affichés dans le fuseau horaire de la propriété',
      missingAvailabilitySingular: 'L\'agent manque de disponibilités pour cette propriété : {names}',
      missingAvailabilityPlural: 'Les agents manquent de disponibilités pour cette propriété : {names}',
      currentProperty: 'Propriété actuelle',
      failedToLoad: 'Impossible de charger la disponibilité de l\'agent',
      agentAvailabilityTitle: 'Disponibilité de l\'agent',
      agentAvailabilityMessage: 'Disponibilité de {agentName}',
      unknownAgent: 'Agent inconnu',
      calendarToday: 'Aujourd\'hui',
      calendarDay: 'Jour',
      calendarWeek: 'Semaine',
      calendarMonth: 'Mois',
    },
  },
} as const;
